import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { 
  ApiResponse, 
  Application, 
  Subscription, 
  Notification, 
  QueueStats, 
  DashboardStats,
  User 
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(username: string, password: string): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.client.post('/admin/login', { username, password });
    return response.data;
  }

  async getMe(): Promise<ApiResponse<User>> {
    const response = await this.client.get('/admin/me');
    return response.data;
  }

  // Dashboard
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.client.get('/admin/stats');
    return response.data;
  }

  // Applications
  async getApplications(page = 1, limit = 10): Promise<ApiResponse<Application[]>> {
    const response = await this.client.get('/admin/applications', { params: { page, limit } });
    return response.data;
  }

  async getApplication(id: string): Promise<ApiResponse<Application>> {
    const response = await this.client.get(`/admin/applications/${id}`);
    return response.data;
  }

  async createApplication(data: Partial<Application>): Promise<ApiResponse<Application>> {
    const response = await this.client.post('/admin/applications', data);
    return response.data;
  }

  async updateApplication(id: string, data: Partial<Application>): Promise<ApiResponse<Application>> {
    const response = await this.client.put(`/admin/applications/${id}`, data);
    return response.data;
  }

  async deleteApplication(id: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete(`/admin/applications/${id}`);
    return response.data;
  }

  async regenerateApiKey(id: string): Promise<ApiResponse<{ apiKey: string }>> {
    const response = await this.client.post(`/admin/applications/${id}/regenerate-key`);
    return response.data;
  }

  // Subscriptions
  async getSubscriptions(applicationId?: string, page = 1, limit = 20): Promise<ApiResponse<Subscription[]>> {
    const response = await this.client.get('/admin/subscriptions', { 
      params: { applicationId, page, limit } 
    });
    return response.data;
  }

  async deleteSubscription(id: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete(`/admin/subscriptions/${id}`);
    return response.data;
  }

  // Notifications
  async getNotifications(applicationId?: string, page = 1, limit = 20): Promise<ApiResponse<Notification[]>> {
    const response = await this.client.get('/admin/notifications', { 
      params: { applicationId, page, limit } 
    });
    return response.data;
  }

  async getNotification(id: string): Promise<ApiResponse<Notification>> {
    const response = await this.client.get(`/admin/notifications/${id}`);
    return response.data;
  }

  async sendNotification(data: {
    applicationId: string;
    title: string;
    body: string;
    icon?: string;
    url?: string;
    type?: 'transactional' | 'marketing';
    filters?: { userId?: string; topics?: string[] };
  }): Promise<ApiResponse<Notification>> {
    const response = await this.client.post('/admin/notifications/send', data);
    return response.data;
  }

  // Queue
  async getQueueStats(): Promise<ApiResponse<QueueStats>> {
    const response = await this.client.get('/admin/queues/stats');
    return response.data;
  }

  async pauseQueue(queueName: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/admin/queues/${queueName}/pause`);
    return response.data;
  }

  async resumeQueue(queueName: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/admin/queues/${queueName}/resume`);
    return response.data;
  }

  async cleanQueue(queueName: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/admin/queues/${queueName}/clean`);
    return response.data;
  }
}

export const api = new ApiService();
export default api;
